import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { FlaskConical, Beaker, HelpCircle, ChevronRight, ChevronLeft, X, Crosshair, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DesignUploadCard } from '@/components/DesignUploadCard';
import { InlineResultSummary } from '@/components/InlineResultSummary';
import SharedNav from '@/components/SharedNav';
import BreedingPipeline from '@/components/BreedingPipeline';

const DESIGNS = [
  { key: 'crd', label: 'CRD', endpoint: '/analyze/crd', desc: 'Completely Randomized Design', color: '#2D6A4F', format: 'Treatment, Replication, Response (one numeric trait per run)' },
  { key: 'rbd', label: 'RBD', endpoint: '/analyze/rbd', desc: 'Randomized Block Design', color: '#2D6A4F', format: 'Treatment, Block, Trait1, Trait2, ... (column names exact, traits flexible)' },
  { key: 'rbd-f1', label: 'RBD Factorial I', endpoint: '/analyze/rbd-factorial-1', desc: 'Two-Factor RBD', color: '#2D6A4F', format: '1 Rep column + 2 factor columns + ≥1 numeric trait. Names and order are flexible — text columns are treated as factors, numeric columns as traits, and Rep is matched by name (case-insensitive). Sample: Variety, Nitrogen, Rep, Yield.' },
  { key: 'rbd-f2', label: 'RBD Factorial II', endpoint: '/analyze/rbd-factorial-2', desc: 'Three-Factor RBD', color: '#2D6A4F', format: '1 Rep column + 3 factor columns + ≥1 numeric trait. Names and order are flexible — text columns are treated as factors, numeric columns as traits, and Rep is matched by name (case-insensitive). Sample: Variety, Nitrogen, Irrigation, Rep, Yield.' },
  { key: 'rbd-f3', label: 'RBD Factorial III', endpoint: '/analyze/rbd-factorial-3', desc: 'Four-Factor RBD', color: '#2D6A4F', format: '1 Rep column + 4 factor columns + ≥1 numeric trait. Names and order are flexible — text columns are treated as factors, numeric columns as traits, and Rep is matched by name (case-insensitive). Sample: Variety, Nitrogen, Phosphorus, Irrigation, Rep, Yield.' },
  { key: 'split', label: 'Split-Plot', endpoint: '/analyze/split-plot', desc: 'Split-Plot Design', color: '#2D6A4F', format: 'Rep, MainPlot, SubPlot, Trait1, ... (column names exact)' },
  { key: 'augmented', label: 'Augmented', endpoint: '/analyze/augmented', desc: 'Augmented Design', color: '#2D6A4F', format: 'Block, Genotype (or Entry), Trait1, ... [IsCheck column — 0/1 — is optional]. Column names case-insensitive.' },
  { key: 'strip-plot', label: 'Strip Plot', endpoint: '/analyze/strip-plot', desc: 'H-strips x V-strips with 3 error terms', color: '#2D6A4F', format: 'HStrip, VStrip, Rep, Trait1, Trait2... (case-insensitive)' },
  { key: 'latin-sq', label: 'Latin Square', endpoint: '/analyze/latin-square', desc: 'Controls row & column variation', color: '#2D6A4F', format: 'Row, Column, Treatment, Trait1, Trait2... (case-insensitive)' },
  { key: 'alpha-lattice', label: 'Alpha Lattice', endpoint: '/analyze/alpha-lattice', desc: 'Incomplete blocks for 20-500 entries', color: '#2D6A4F', format: 'Genotype, Rep, Block, Trait1, Trait2... (case-insensitive)' },
  { key: 'missing-plot', label: 'Missing Plot', endpoint: '/analyze/missing-plot', desc: 'Yates estimation for incomplete data', color: '#2D6A4F', format: 'Treatment (or Genotype), Rep (or Block), Trait1... (NA for missing values; case-insensitive)' },
  { key: 'strip-split', label: 'Strip-Split Plot', endpoint: '/analyze/strip-split-plot', desc: '3 factors in strip+subplot', color: '#2D6A4F', format: 'HStrip, VStrip, SubPlot, Rep, Trait1... (case-insensitive)' },
  { key: 'bib', label: 'BIB Design', endpoint: '/analyze/bib-design', desc: 'Treatments in partial blocks', color: '#2D6A4F', format: 'Block, Treatment, Trait1, Trait2... (case-insensitive)' },
  { key: 'nested', label: 'Nested Design', endpoint: '/analyze/nested-design', desc: 'Subsamples within treatments', color: '#2D6A4F', format: 'Treatment, Subgroup, Trait1, Trait2... (case-insensitive)' },
];

const WIZARD_Q = [
  { q: 'How many factors?', opts: [
    { label: '1 factor', val: 1 }, { label: '2 factors', val: 2 }, { label: '3 factors', val: 3 }
  ]},
  { q: 'Can all treatments fit in one block?', opts: [
    { label: 'Yes — complete blocks', val: 'complete' }, { label: 'No — incomplete blocks', val: 'incomplete' }, { label: 'Not sure', val: 'unsure' }
  ]},
  { q: 'Do you have >20 genotypes?', opts: [
    { label: 'Yes', val: true }, { label: 'No', val: false }
  ]},
];

function getRecommendation(answers) {
  const [factors, blocks, large] = answers;
  if (factors === 1 && blocks === 'incomplete' && large) return 'alpha-lattice';
  if (factors === 1 && blocks === 'incomplete') return 'bib';
  if (factors === 1 && blocks === 'complete') return 'rbd';
  if (factors === 2 && blocks === 'complete') return 'split';
  if (factors === 2 && blocks === 'incomplete') return 'strip-plot';
  if (factors === 3) return 'strip-split';
  if (large) return 'augmented';
  return 'rbd';
}

export default function ResearchDesign() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('crd');
  const [analysisIds, setAnalysisIds] = useState({});
  const [freshResults, setFreshResults] = useState({});
  const [exporting, setExporting] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState([]);
  const [recommended, setRecommended] = useState(null);
  const [showPipeline, setShowPipeline] = useState(false);
  const tabRef = useRef(null);

  const handleResult = (data, designKey) => {
    if (data?.id) {
      setAnalysisIds(prev => ({ ...prev, [designKey]: data.id }));
      setFreshResults(prev => ({ ...prev, [designKey]: data }));
    }
  };

  const handleExport = async (analysisId, format) => {
    const key = `${analysisId}-${format}`;
    setExporting(key);
    toast.info(`Generating ${format.toUpperCase()}...`);
    try {
      const response = await axios.post(`${API}/generate-document/${analysisId}`, { format },
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BKAgriD_Report.${format}`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch { toast.error('Export failed.'); }
    finally { setExporting(null); }
  };

  const handleWizardAnswer = (val) => {
    const newAnswers = [...wizardAnswers, val];
    setWizardAnswers(newAnswers);
    if (newAnswers.length >= 3) {
      const rec = getRecommendation(newAnswers);
      setRecommended(rec);
      setActiveTab(rec);
      setWizardOpen(false);
      setWizardStep(0);
      setWizardAnswers([]);
      toast.success(`Recommended: ${DESIGNS.find(d => d.key === rec)?.label || rec}`);
    } else {
      setWizardStep(wizardStep + 1);
    }
  };

  const scrollTabs = (dir) => {
    if (tabRef.current) tabRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="research-design-page">
      <SharedNav />

      <div className="max-w-[1320px] mx-auto px-6 page-enter" style={{ paddingTop: 32, paddingBottom: 48 }}>
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div style={{ borderLeft: '4px solid var(--green-500)', paddingLeft: 16 }}>
            <h1 className="font-display flex items-center gap-2" style={{ fontSize: 28, color: 'var(--green-900)' }}>
              <FlaskConical size={24} style={{ color: 'var(--green-500)' }} />
              Experimental Designs
            </h1>
            <p className="font-body mt-1" style={{ fontSize: 15, color: 'var(--text-medium)' }}>
              Upload data for specific research designs. Each design requires a specific column format.
            </p>
          </div>
          <button
            onClick={() => { setWizardOpen(!wizardOpen); setWizardStep(0); setWizardAnswers([]); }}
            className="flex items-center gap-1.5 font-body btn-press transition-all shrink-0"
            style={{
              fontSize: 13, fontWeight: 600, padding: '10px 20px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--green-900)', color: 'white',
            }}
            data-testid="design-wizard-btn"
          >
            <Crosshair size={15} /> Which Design?
          </button>
        </div>

        {/* Pipeline Banner */}
        {!showPipeline && (
          <div className="rounded-xl p-4 mb-6 flex items-center justify-between fade-in" style={{ background: 'linear-gradient(135deg, #1B4332, #2D6A4F)', boxShadow: '0 4px 16px rgba(27,67,50,0.2)' }} data-testid="pipeline-banner">
            <div>
              <p className="font-body text-sm font-bold text-white">Run Complete Breeding Pipeline</p>
              <p className="font-body text-xs text-white/70">Get all analyses done automatically — ANOVA, Heritability, Correlation, Path, Selection Index, Master Report</p>
            </div>
            <button onClick={() => setShowPipeline(true)}
              className="flex items-center gap-2 font-body btn-press shrink-0"
              style={{ fontSize: 13, fontWeight: 700, padding: '10px 20px', borderRadius: 'var(--radius-md)', background: 'white', color: '#1B4332' }}
              data-testid="open-pipeline-btn">
              <Link2 size={15} /> Launch Pipeline
            </button>
          </div>
        )}

        {/* Pipeline Panel */}
        {showPipeline && (
          <div className="bg-white rounded-2xl p-6 mb-6" style={{ border: '2px solid var(--green-500)', boxShadow: '0 8px 32px rgba(27,67,50,0.12)' }}>
            <div className="flex items-center justify-between mb-4">
              <span />
              <button onClick={() => setShowPipeline(false)} className="p-1.5 rounded-lg hover:bg-gray-100 transition"><X size={16} style={{ color: 'var(--text-light)' }} /></button>
            </div>
            <BreedingPipeline />
          </div>
        )}

        {/* Design Selector Wizard */}
        {wizardOpen && (
          <div className="bg-white rounded-xl p-5 mb-6 fade-in" style={{ border: '1px solid var(--green-500)', boxShadow: '0 4px 16px rgba(27,67,50,0.1)' }} data-testid="design-wizard">
            <button onClick={() => setWizardOpen(false)} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-gray-100 transition" style={{ position: 'relative', float: 'right' }}><X size={16} style={{ color: 'var(--text-light)' }} /></button>
            <h3 className="font-body mb-2" style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)' }}>Which Design Should I Use?</h3>
            <p className="font-body mb-3" style={{ fontSize: 12, color: 'var(--text-light)' }}>Question {wizardStep + 1} of 3</p>
            <p className="font-body mb-3" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-dark)' }}>{WIZARD_Q[wizardStep].q}</p>
            <div className="flex gap-2 flex-wrap">
              {WIZARD_Q[wizardStep].opts.map((opt, i) => (
                <button key={i} onClick={() => handleWizardAnswer(opt.val)}
                  className="font-body btn-press transition-all"
                  style={{
                    padding: '8px 16px', fontSize: 13, borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-color)', background: 'white', color: 'var(--text-dark)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green-500)'; e.currentTarget.style.background = 'var(--green-50)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.background = 'white'; }}
                >{opt.label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Navigation — Single scrollable row */}
        <div className="relative mb-6">
          <button onClick={() => scrollTabs(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white flex items-center justify-center transition-opacity hover:shadow-md" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <ChevronLeft size={16} style={{ color: 'var(--text-medium)' }} />
          </button>
          <div ref={tabRef} className="overflow-x-auto no-scrollbar mx-10 tab-scroll-container" style={{ scrollBehavior: 'smooth' }}>
            <div className="flex gap-1 p-1.5 bg-white rounded-xl" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', width: 'max-content' }}>
              {DESIGNS.map(d => {
                const isActive = activeTab === d.key;
                return (
                  <button
                    key={d.key}
                    onClick={() => setActiveTab(d.key)}
                    className="flex items-center gap-1.5 font-body whitespace-nowrap btn-press transition-all"
                    style={{
                      padding: '8px 18px', borderRadius: 'var(--radius-md)',
                      fontSize: 13, fontWeight: 500,
                      background: isActive ? 'white' : 'transparent',
                      color: isActive ? 'var(--green-900)' : 'var(--text-medium)',
                      boxShadow: isActive ? '0 1px 6px rgba(0,0,0,0.12)' : 'none',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F0F7F3'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    data-testid={`design-tab-${d.key}`}
                  >
                    <Beaker size={13} />
                    {d.label}
                    {recommended === d.key && (
                      <span className="ml-1 px-1.5 py-0.5 font-body animate-pulse" style={{ fontSize: 8, fontWeight: 700, background: '#FEF3C7', color: '#B45309', borderRadius: 99 }}>REC</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <button onClick={() => scrollTabs(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white flex items-center justify-center transition-opacity hover:shadow-md" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <ChevronRight size={16} style={{ color: 'var(--text-medium)' }} />
          </button>
        </div>

        {/* Active Design Card */}
        {DESIGNS.filter(d => d.key === activeTab).map(d => (
          <div key={d.key} className="bg-white rounded-2xl fade-in" style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
            <div className="p-6 pb-0">
              <div className="flex items-center gap-3 mb-5" data-testid={`design-title-${d.key}`}>
                <div className="flex items-center justify-center font-body" style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: 'var(--green-900)', color: 'white', fontSize: 18, fontWeight: 700,
                }}>
                  {d.label.charAt(0)}
                </div>
                <div>
                  <div className="font-body" style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-dark)' }}>{d.label}</div>
                  <div className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>{d.desc}</div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6">
              <DesignUploadCard
                key={d.key}
                design={d}
                onResult={(data) => handleResult(data, d.key)}
                analysisId={analysisIds[d.key]}
                exporting={exporting}
                onExport={handleExport}
              />
              {/* Sub-Batch 3R Layer 3: inline result summary for parity
                  with Biometrical Analysis page. */}
              {freshResults[d.key] && (
                <InlineResultSummary
                  data={freshResults[d.key]}
                  designKey={d.key}
                  designLabel={d.label}
                  color="#2D6A4F"
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
